import { computed, inject, InjectionToken, Injector, Signal, signal, TemplateRef, untracked } from '@angular/core';
import { effectiveSettings } from './dashboard-widget-settings';
import { DashboardWidgetInstance } from '../layout/dashboard-layout';
import { DashboardWidgetDefinition } from './dashboard-widget-definition';

/** The frame draws every state the same way for all widgets. `ready` is the default for widgets that never report. */
export type DashboardWidgetDataState =
  | { status: 'ready' }
  | { status: 'loading' }
  /** Loaded, but nothing to show; `message` (a translation key or text) defaults to "Nothing to show yet." */
  | { status: 'empty'; message?: string }
  /** `message` (a translation key or text) defaults to "Couldn't load this widget's data."; `retry` adds a Try again button */
  | { status: 'failed'; message?: string; retry?: () => void };

/** What a widget body knows of its placement and may tell the frame; a settings component gets its own handle. */
export interface DashboardWidgetHandle<S = unknown> {
  readonly definition: DashboardWidgetDefinition<S>;
  readonly instance: Signal<DashboardWidgetInstance<S>>;
  /** The definition's defaults merged with the instance's settings */
  readonly settings: Signal<S>;
  /** Customize mode is on; always true in a settings component */
  readonly editing: Signal<boolean>;
  setSubtitle(text: string | null): void;
  /** Reports the widget's data state; `dashboardWidgetResource` does this for you */
  setDataState(state: DashboardWidgetDataState): void;
}

/** The frame's side of a widget: what the widget reported, and where its header actions go. */
export interface DashboardWidgetPort<S = unknown> extends DashboardWidgetHandle<S> {
  readonly subtitle: Signal<string | null>;
  readonly headerActions: Signal<TemplateRef<unknown> | null>;
  readonly dataState: Signal<DashboardWidgetDataState>;
  /** The last finished load left content on screen, so the next load refreshes over it */
  readonly hasShownData: Signal<boolean>;
  setHeaderActions(template: TemplateRef<unknown> | null): void;
}

export const DASHBOARD_WIDGET = new InjectionToken<DashboardWidgetPort>('DASHBOARD_WIDGET');

export function injectDashboardWidget<S>(): DashboardWidgetHandle<S> {
  return inject(DASHBOARD_WIDGET) as DashboardWidgetPort<S>;
}

export function createDashboardWidgetPort<S>(
  definition: DashboardWidgetDefinition<S>,
  instance: Signal<DashboardWidgetInstance<S>>,
  editing: Signal<boolean>,
): DashboardWidgetPort<S> {
  const subtitle = signal<string | null>(null);
  const headerActions = signal<TemplateRef<unknown> | null>(null);
  const dataState = signal<DashboardWidgetDataState>({ status: 'ready' });
  const hasShownData = signal(false);
  return {
    definition,
    instance,
    settings: computed(() => effectiveSettings(definition, instance().settings) as S),
    editing,
    subtitle: subtitle.asReadonly(),
    headerActions: headerActions.asReadonly(),
    dataState: dataState.asReadonly(),
    hasShownData: hasShownData.asReadonly(),
    setSubtitle: (text) => subtitle.set(text),
    setHeaderActions: (template) => headerActions.set(template),
    setDataState: (state) => {
      untracked(() => hasShownData.set(nextHasShownData(hasShownData(), dataState().status, state.status)));
      dataState.set(state);
    },
  };
}

/**
 * Empty and failed results leave nothing worth keeping. The initial `ready` is only the default for widgets that
 * never report, so `ready` counts only after `loading`.
 */
function nextHasShownData(
  shownBefore: boolean,
  previous: DashboardWidgetDataState['status'],
  status: DashboardWidgetDataState['status'],
): boolean {
  switch (status) {
    case 'loading':
      return shownBefore;
    case 'ready':
      return shownBefore || previous === 'loading';
    case 'empty':
    case 'failed':
      return false;
  }
}

/** The injector a widget body or settings component is created with, so it can `injectDashboardWidget()` */
export function widgetInjector(port: DashboardWidgetPort, parent: Injector): Injector {
  return Injector.create({ providers: [{ provide: DASHBOARD_WIDGET, useValue: port }], parent });
}
